import {
	emailHref,
	MAIN_SITE_LABEL,
	ORGANIZATION,
	PRODUCT,
} from "@nwga/contracts/organization";
import { COUNTIES, REGION_NAME } from "@nwga/contracts/region";
import { Button } from "@nwga/ui/components/button";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ProsePage } from "../components/prose-page.tsx";

export const Route = createFileRoute("/about")({ component: AboutPage });

const COUNTY_LIST = COUNTIES.map((county) => county.name).join(", ");

/**
 * What this platform is.
 *
 * Carefully *not* a page about the Authority — that page exists, on
 * northwestgeorgia.us, and duplicating it here would give the region two
 * competing introductions to the same organisation. This answers the narrower
 * question a visitor to this origin actually has: what is this site, who runs
 * it, what does it cost, and what will it do next.
 *
 * The "not the only one" framing is load-bearing. Job listings are the first
 * service on Connect, and copy that describes the site *as* a job board is copy
 * that has to be rewritten the day the second one ships.
 */
function AboutPage() {
	return (
		<ProsePage
			title={`About ${PRODUCT.name}`}
			eyebrow={`A service of the ${ORGANIZATION.shortName}`}
			intro={`A free platform connecting people and employers across ${REGION_NAME} — starting with job listings.`}
			actions={
				<>
					<Button asChild size="lg">
						<Link to="/jobs">Browse jobs</Link>
					</Button>
					<Button asChild size="lg" variant="outline">
						<Link to="/businesses/register">Register your business</Link>
					</Button>
				</>
			}
		>
			<h2>What this is</h2>
			<p>
				{PRODUCT.name} is an online platform run by the {ORGANIZATION.name} for
				the people and businesses of {COUNTY_LIST} counties. It exists so that
				work available in the region is easy to find from the region, and so
				that a local employer has somewhere to post an opening without paying a
				national job site to reach its own neighbours.
			</p>

			<h2>What it does today</h2>
			<p>
				Right now, {PRODUCT.name} does one thing:{" "}
				<Link to="/jobs">job listings</Link>. Any business operating in the
				region can register, and once the Authority has reviewed the
				registration — usually within two business days — it can publish as many
				openings as it has. Listings appear on the public board immediately, and
				applications go directly to the employer. Browsing and applying need no
				account at all.
			</p>

			<h2>What it will do</h2>
			<p>
				Job listings are the first service here, not the only one planned. The
				Authority does a great deal for employers and residents in {REGION_NAME}{" "}
				— workforce programmes, site and building information, business support
				— and this platform is where the parts of that work which belong online
				will be added over time. When a new service arrives it will show up in
				the navigation above; nothing you already use will move.
			</p>

			<h2>What it costs</h2>
			<p>
				Nothing, to anyone. Browsing is free, applying is free, registering a
				business is free, and posting listings is free — there is no premium
				tier, no featured placement, and no fee for an employer of any size.{" "}
				{PRODUCT.name} is funded as part of the Authority's work for the region,
				not by the people using it. No listing may charge an applicant either;
				see the <Link to="/terms">terms of use</Link>.
			</p>

			<h2>Who runs it</h2>
			<p>
				The {ORGANIZATION.name} is the joint economic development organisation
				for {COUNTY_LIST} counties. Its own site,{" "}
				<a href={ORGANIZATION.url} target="_blank" rel="noreferrer">
					{MAIN_SITE_LABEL}
				</a>
				, is the place to read about the Authority itself, its communities and
				its work with employers considering the region. {PRODUCT.name} is the
				part of that work which needed an account and a database, so it lives on
				its own address.
			</p>

			<h2>Getting in touch</h2>
			<p>
				For anything about this platform — a listing that looks wrong, a
				registration that is taking too long, an accessibility problem, or a
				suggestion — use the <Link to="/contact">contact page</Link>. For
				anything about the Authority itself, write to{" "}
				<a href={emailHref}>{ORGANIZATION.contact.email}</a>.
			</p>
		</ProsePage>
	);
}
